import {Grid} from "@mui/material";
import React from "react";
import {useTranslation} from "react-i18next";
import {FormAutocomplete, FormCustomSwitch, FormDateTimePicker, FormTextField,} from "../../../common/form-fields";
import {useRecoilState} from "recoil";
import {unitsEnumAtom} from "../../../state/rentalPeriodsRecoil";
import "dayjs/locale/it";
import {useFormContext, useWatch} from "react-hook-form";
import {FormRentalPeriodModel} from "../../../api/types/rentalPeriods";
import {setFormNumberValue} from "../../../utils";
import {numberIntegerRegex} from "../../../constants";
import dayjs from "dayjs";

const FormRentalPeriod = ({readonly}: { readonly: boolean }) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Recoil
    const [unitsEnum] = useRecoilState(unitsEnumAtom);

    // Use Form Context
    const {register, setValue, watch} = useFormContext<FormRentalPeriodModel>();

    const tpUnitLabel = useWatch({
        name: "tpUnit",
    });

    const daysOfTheWeekUppercase = [
        {code: 1, label: dayjs().day(1).format("dddd").toUpperCase()},
        {code: 2, label: dayjs().day(2).format("dddd").toUpperCase()},
        {code: 3, label: dayjs().day(3).format("dddd").toUpperCase()},
        {code: 4, label: dayjs().day(4).format("dddd").toUpperCase()},
        {code: 5, label: dayjs().day(5).format("dddd").toUpperCase()},
        {code: 6, label: dayjs().day(6).format("dddd").toUpperCase()},
        {code: 7, label: dayjs().day(0).format("dddd").toUpperCase()},
    ];

    console.log(watch());

    return (
        <>
            <Grid container spacing={2} alignItems="start">
                <Grid
                    id={"form"}
                    container
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    spacing={2}
                    rowSpacing={3}
                >
                    {/* NAME RENTAL PERIOD */}
                    <Grid item xs={12} md={3}>
                        <FormTextField
                            {...register("name")}
                            label={`${t("pricing:rentalPeriods.form.labels.name")}*`}
                            readOnly={readonly}
                        />
                    </Grid>
                    {/* UNIT RENTAL PERIOD */}
                    <Grid item xs={12} md={3}>
                        <FormAutocomplete
                            {...register("tpUnit")}
                            label={`${t("pricing:rentalPeriods.form.labels.unit")}*`}
                            options={unitsEnum}
                            isOptionEqualToValue={(option: string, value: string) =>
                                option === value
                            }
                            readOnly={readonly}
                            handleChange={(value: string) => {
                                setValue("tpUnit", value);
                                setValue("weekend", false);
                                setValue("rentalPeriodFrom", "");
                                setValue("rentalPeriodTo", "");
                                setValue("calendarFrom", null);
                                setValue("calendarTo", null);
                            }}
                        />
                    </Grid>
                    {/* RANGE RENTAL PERIOD */}
                    <Grid item xs={12}>
                        <Grid container item xs={12} spacing={2}>
                            {/* FROM */}
                            {watch("weekend") ? (
                                <Grid item xs={12} md={3}>
                                    <FormAutocomplete
                                        {...register("rentalPeriodFrom")}
                                        label={`${t("pricing:rentalPeriods.form.labels.from")}*`}
                                        readOnly={readonly}
                                        options={daysOfTheWeekUppercase}
                                        isOptionEqualToValue={(option, value) =>
                                            option?.code === value?.code
                                        }
                                    />
                                </Grid>
                            ) : tpUnitLabel === "ABSOLUTE" ? (
                                <Grid item xs={12} md={3}>
                                    <FormDateTimePicker
                                        {...register("calendarFrom")}
                                        label={`${t("pricing:rentalPeriods.form.labels.from")}*`}
                                        readOnly={readonly}
                                        minDate={dayjs()}
                                    />
                                </Grid>
                            ) : (
                                <Grid item xs={12} md={3}>
                                    <FormTextField
                                        {...register("rentalPeriodFrom")}
                                        label={`${t("pricing:rentalPeriods.form.labels.from")}*`}
                                        readOnly={readonly}
                                        type={"text"}
                                        handleChange={(value: string) =>
                                            setFormNumberValue(
                                                value,
                                                setValue,
                                                "rentalPeriodFrom",
                                                numberIntegerRegex
                                            )
                                        }
                                    />
                                </Grid>
                            )}
                            {/* TO */}
                            {watch("weekend") ? (
                                <Grid item xs={12} md={3}>
                                    <FormAutocomplete
                                        {...register("rentalPeriodTo")}
                                        label={`${t("pricing:rentalPeriods.form.labels.to")}*`}
                                        readOnly={readonly}
                                        options={daysOfTheWeekUppercase}
                                        isOptionEqualToValue={(option, value) =>
                                            option?.code === value?.code
                                        }
                                    />
                                </Grid>
                            ) : tpUnitLabel === "ABSOLUTE" ? (
                                <Grid item xs={12} md={3}>
                                    <FormDateTimePicker
                                        {...register("calendarTo")}
                                        label={`${t("pricing:rentalPeriods.form.labels.to")}*`}
                                        readOnly={readonly}
                                        minDate={watch("calendarFrom")}
                                    />
                                </Grid>
                            ) : (
                                <Grid item xs={12} md={3}>
                                    <FormTextField
                                        {...register("rentalPeriodTo")}
                                        label={`${t("pricing:rentalPeriods.form.labels.to")}*`}
                                        readOnly={readonly}
                                        type={"text"}
                                        handleChange={(value: string) =>
                                            setFormNumberValue(
                                                value,
                                                setValue,
                                                "rentalPeriodTo",
                                                numberIntegerRegex
                                            )
                                        }
                                    />
                                </Grid>
                            )}
                            {/* DESCRIPTION RENTAL PERIOD */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("description")}
                                    label={t("pricing:rentalPeriods.form.labels.description")}
                                    readOnly={readonly}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    {/* ACTIVE RENTAL PERIOD */}
                    <Grid item xs={12} md={3}>
                        <FormCustomSwitch
                            {...register("active")}
                            label={t("pricing:rentalPeriods.form.labels.active")}
                            readOnly={readonly}
                        />
                    </Grid>
                    {/* WEEKEND */}
                    <Grid item xs={12} md={3}>
                        <FormCustomSwitch
                            {...register("weekend")}
                            label={t("pricing:rentalPeriods.form.labels.weekend")}
                            readOnly={tpUnitLabel !== "DAY" || readonly}
                            handleChange={(value: boolean) => {
                                setValue("weekend", value);
                                setValue("rentalPeriodFrom", null);
                                setValue("rentalPeriodTo", null);
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default FormRentalPeriod;
